<template>
  <div class="p-6 md:p-10 bg-[#F8FAFC] min-h-screen font-sans text-slate-800">
    
    <header class="flex lg:flex-row justify-between items-start lg:items-center mb-10 gap-6">
      <div class="flex items-center gap-5">
        <div class="bg-indigo-600 p-2.5 rounded-xl shadow-lg shadow-indigo-100">
          <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-7h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div>
          <h1 class="text-xl font-bold tracking-tight text-slate-900">Configuration Agence</h1>
          <div class="flex items-center gap-1 mt-0.5">
            <select v-model="selectedAgencyId" @change="fetchData" class="bg-transparent border-none text-indigo-600 font-semibold p-0 focus:ring-0 cursor-pointer text-sm">
              <option v-for="agency in agencies" :key="agency.id" :value="agency.id">{{ agency.nom }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3  lg:w-auto">
        <button v-if="activeTab === 'queues'" @click="showAddQueueModal = true" class="flex-1 lg:flex-none px-4 py-2.5 bg-white border border-slate-200 rounded-lg font-bold text-xs text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
          <UIIcon name="ticket" :size="16" />
          Créer une File
        </button>
        <button v-if="activeTab === 'users'" @click="showAddUserModalState = true" class="flex-1 lg:flex-none px-4 py-2.5 bg-white border border-slate-200 rounded-lg font-bold text-xs text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
          <UIIcon name="user" :size="16" />
          Ajouter Personnel
        </button>
        <button v-if="activeTab === 'counters'" @click="showAddCounterModal = true" class="flex-1 lg:flex-none px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-bold text-xs shadow-sm hover:bg-indigo-700 transition-all shadow-indigo-100">
          + Nouveau Guichet
        </button>
      </div>
    </header>

    <div class="grid  lg:grid-cols-4 gap-5 mb-10">
      <div v-for="(stat, i) in [
        { label: 'Guichets', val: counters.length, color: 'text-slate-900', bg: 'bg-white' },
        { label: 'Files (Services)', val: queues.length, color: 'text-orange-600', bg: 'bg-white' },
        { label: 'Personnel Total', val: users.length, color: 'text-indigo-600', bg: 'bg-white' },
        { label: 'Agents Libres', val: users.filter(u => !u.assignedCounterId).length, color: 'text-emerald-600', bg: 'bg-white' }
      ]" :key="i" class="p-6 rounded-2xl border border-slate-100 shadow-sm transition-all hover:shadow-md" :class="stat.bg">
        <p class="text-[10px] font-bold uppercase text-slate-400 tracking-widest">{{ stat.label }}</p>
        <p class="text-3xl font-bold mt-1" :class="stat.color">{{ stat.val }}</p>
      </div>
    </div>

    <div class="flex gap-10 mb-8 border-b border-slate-200 px-4">
      <button @click="activeTab = 'counters'" :class="activeTab === 'counters' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-4 font-bold' : 'text-slate-400 pb-4 font-semibold hover:text-slate-600'" class="text-xs uppercase tracking-widest transition-all flex items-center gap-2 outline-none">
        <UIIcon name="archive" :size="16" />
        Guichets
      </button>
      <button @click="activeTab = 'queues'" :class="activeTab === 'queues' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-4 font-bold' : 'text-slate-400 pb-4 font-semibold hover:text-slate-600'" class="text-xs uppercase tracking-widest transition-all flex items-center gap-2 outline-none">
        <UIIcon name="ticket" :size="16" />
        Files d'attente
      </button>
      <button @click="activeTab = 'users'" :class="activeTab === 'users' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-4 font-bold' : 'text-slate-400 pb-4 font-semibold hover:text-slate-600'" class="text-xs uppercase tracking-widest transition-all flex items-center gap-2 outline-none">
        <UIIcon name="users" :size="16" />
        Personnel
      </button>
    </div>

    <main>
      
      <div v-if="activeTab === 'counters'" class="space-y-4">
        <div v-for="counter in counters" :key="counter._id" class="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex md:flex-row md:items-center justify-between gap-4 group hover:border-indigo-200 transition-all">
          <div class="flex items-center gap-6">
            <div class="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center font-bold text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
              {{ counter.priorityOrder || 0 }}
            </div>
            <div>
              <h3 class="text-base font-bold text-slate-900">{{ counter.name }}</h3>
              <div class="flex flex-wrap gap-2 mt-2">
                <span v-if="counter.defaultQueueId" class="text-[9px] bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-md font-bold border border-indigo-100 flex items-center gap-1">
                  <UIIcon name="star" :size="10" />
                  {{ getQueueName(counter.defaultQueueId) }} (Défaut)
                </span>
                <span v-for="sId in counter.serviceIds" :key="sId" v-show="sId !== counter.defaultQueueId" class="text-[9px] bg-slate-50 text-slate-500 px-2.5 py-1 rounded-md font-bold border border-slate-100">
                  {{ getQueueName(sId) }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-6">
            <div class="hidden md:block text-right">
              <p class="text-[10px] font-bold text-slate-400 uppercase">Status</p>
              <p class="text-xs font-semibold text-slate-600">{{ getCounterUsers(counter._id).length }} agent(s) rattaché(s)</p>
            </div>
            <button @click="openEditDrawer(counter)" class="px-5 py-2.5 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-indigo-600 transition-all shadow-sm">
              Configurer
            </button>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'queues'" class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="queue in queues" :key="queue._id" class="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden group hover:border-orange-200 transition-all">
          <div v-if="queue.icon" class="absolute top-0 right-0 w-16 h-16 bg-orange-50 rounded-bl-3xl flex items-center justify-center transition-colors group-hover:bg-orange-100">
            <PharmacyIcon :name="queue.icon" :size="28" color="#f97316" />
          </div>
          <h3 class="text-lg font-bold text-slate-900 mb-1 capitalize">{{ queue.name }}</h3>
          <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Préfixe : {{ queue.prefix }}-XXX</p>

          <div class="mt-8 flex justify-between items-center border-t border-slate-50 pt-4">
            <button @click="deleteQueue(queue._id)" class="text-xs font-bold text-slate-300 hover:text-red-500 transition-colors">Supprimer</button>
            <button @click="openEditQueueModal(queue)" class="px-4 py-2 bg-slate-50 hover:bg-orange-500 hover:text-white rounded-lg font-bold text-xs transition-all border border-slate-100">
              Modifier
            </button>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'users'" class="space-y-4">
        <div class="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-4 items-center">
          <div class="flex-1 relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </span>
            <input type="text" v-model="userSearchQuery" placeholder="Rechercher un membre du personnel..." class="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm outline-none">
          </div>
          <button v-if="userSearchQuery" @click="userSearchQuery = ''" class="px-4 py-2.5 text-slate-400 hover:text-slate-600 font-bold text-xs">
            Effacer
          </button>
        </div>

        <div class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <table class="w-full text-left">
            <thead class="bg-slate-50/50 text-[10px] font-bold uppercase text-slate-400 border-b border-slate-100">
              <tr>
                <th class="px-8 py-4">Collaborateur</th>
                <th class="px-6 py-4">Rôle Système</th>
                <th class="px-6 py-4">Poste Assigné</th>
                <th class="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50">
              <tr v-for="user in paginatedUsers" :key="user._id" class="hover:bg-slate-50/30 transition-colors">
                <td class="px-8 py-4">
                  <div class="font-bold text-sm text-slate-900">{{ user.firstName }} {{ user.lastName }}</div>
                  <div class="text-[10px] text-slate-400 font-medium">{{ user.email }}</div>
                </td>
                <td class="px-6 py-4">
                  <span :class="user.role === 'AGENCY_ADMIN' ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-blue-50 text-blue-600 border-blue-100'" 
                        class="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase border">
                    {{ user.role.replace('AGENCY_', '') }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <select @change="updateUserCounter(user, $event)" class="bg-slate-50 border border-slate-100 rounded-lg text-[11px] font-bold py-1.5 px-3 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                    <option :value="null">Non assigné</option>
                    <option v-for="c in counters" :key="c._id" :value="c._id" :selected="user.assignedCounterId === c._id">{{ c.name }}</option>
                  </select>
                </td>
                <td class="px-8 py-4 text-right">
                  <div class="flex justify-end gap-3">
                    <button @click="resetUserPassword(user)" class="p-2 text-slate-400 hover:text-orange-500 transition-all" title="Réinitialiser">
                      <UIIcon name="key" :size="16"/>
                    </button>
                    <button v-if="user.role !== 'AGENCY_ADMIN'" @click="deleteUser(user._id)" class="p-2 text-slate-300 hover:text-red-500 transition-all">
                      <UIIcon name="trash" :size="16" />
                    </button>
                    <span v-else class="p-2 text-slate-100 cursor-not-allowed">
                      <UIIcon name="lock" :size="16" />
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <TablePagination v-if="filteredUsers.length > 0" :current-page="userCurrentPage" @update:current-page="userCurrentPage = $event" :total-items="filteredUsers.length" :items-per-page="userItemsPerPage" class="border-t border-slate-50" />
        </div>
      </div>
    </main>

    <div v-if="selectedCounter" class="fixed inset-0 z-[100] overflow-hidden">
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" @click="selectedCounter = null"></div>
      <div class="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl p-10 overflow-y-auto transform transition-transform duration-300 border-l border-slate-100">
        <header class="flex justify-between items-center mb-10">
          <div>
            <h2 class="text-2xl font-bold text-slate-900">{{ selectedCounter.name }}</h2>
            <p class="text-xs text-slate-400 font-medium">Paramètres avancés du guichet</p>
          </div>
          <button @click="selectedCounter = null" class="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-50 text-slate-400">✕</button>
        </header>

        <div class="space-y-8">
          <div>
            <label class="block text-[10px] font-bold text-slate-400 mb-2 uppercase tracking-widest">Nom d'affichage</label>
            <input v-model="selectedCounter.name" type="text" class="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all" />
          </div>

          <div>
            <label class="block text-[10px] font-bold text-slate-400 mb-3 uppercase tracking-widest">Files gérées (Priorité par ordre)</label>
            <div class="grid gap-2">
              <div v-for="q in queues" :key="q._id"
                @click="toggleQueueToCounter(q._id)"
                :class="selectedCounter.serviceIds?.includes(q._id) ? 'border-indigo-500 bg-indigo-50/50 text-indigo-700' : 'border-slate-100 bg-slate-50 text-slate-400'"
                class="p-4 rounded-xl border-2 cursor-pointer flex justify-between items-center transition-all group hover:border-indigo-200"
              >
                <div class="flex items-center gap-3">
                  <div class="w-2 h-2 rounded-full" :class="selectedCounter.serviceIds?.includes(q._id) ? 'bg-indigo-500' : 'bg-slate-200'"></div>
                  <span class="font-bold text-sm">{{ q.name }}</span>
                </div>
                <span v-if="selectedCounter.serviceIds?.includes(q._id)" class="text-[9px] bg-indigo-600 text-white px-2.5 py-1 rounded-lg font-bold">
                  PRIO {{ selectedCounter.serviceIds.indexOf(q._id) + 1 }}
                </span>
              </div>
            </div>
          </div>

          <div class="pt-6 border-t border-slate-100">
            <label class="block text-[10px] font-bold text-slate-400 mb-2 uppercase tracking-widest">File d'Appel par défaut</label>
            <select v-model="selectedCounter.defaultQueueId" @change="updateDefaultQueue" class="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/20">
              <option :value="null">Rotation intelligente (Priorités)</option>
              <option v-for="q in queues" :key="q._id" :value="q._id">{{ q.name }} ({{ q.prefix }})</option>
            </select>
            <p class="text-[10px] text-slate-400 mt-2 italic px-1">Si définie, le guichet traitera cette file en priorité absolue peu importe l'ordre.</p>
          </div>

          <div class="pt-10 flex gap-4">
             <button @click="deleteCounter(selectedCounter._id)" class="px-6 py-4 text-red-500 font-bold text-[10px] uppercase tracking-widest hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-100">Supprimer</button>
             <button @click="updateCounter" class="flex-1 py-4 bg-indigo-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all">Enregistrer les modifications</button>
          </div>
        </div>
      </div>
    </div>

<div v-if="showAddQueueModal || showEditQueueModal" class="fixed inset-0 z-[110] flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" @click="showAddQueueModal = false; showEditQueueModal = false"></div>
  
  <div class="relative bg-white w-full max-w-sm rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
    <div class="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
      <div class="flex items-center gap-2">
        <div class="p-1.5 bg-indigo-100 rounded-lg">
          <UIIcon name="ticket" color="#4f46e5" :size="16"/>
        </div>
        <h2 class="text-sm font-bold text-slate-800">{{ showEditQueueModal ? 'Modifier' : 'Nouveau' }} Service</h2>
      </div>
      <button @click="showAddQueueModal = false; showEditQueueModal = false" class="text-slate-400 hover:text-slate-600 transition-colors text-lg">✕</button>
    </div>

    <div class="p-5 space-y-4">
      <div class="grid grid-cols-4 gap-3">
        <div class="col-span-3">
          <label class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Nom du service</label>
          <input v-model="(showEditQueueModal ? editingQueue : newQueue).name" placeholder="ex: Pharmacie" class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all" />
        </div>
        <div>
          <label class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block text-center">Code</label>
          <input v-model="(showEditQueueModal ? editingQueue : newQueue).prefix" maxlength="3" class="w-full bg-white border border-slate-200 rounded-lg py-2 text-xs font-black text-center uppercase focus:border-indigo-500 outline-none transition-all" placeholder="A" />
        </div>
      </div>

      <div>
        <label class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Icône</label>
        <p class="text-[9px] text-slate-400 mb-2 italic">Cliquez sur une icône pour la sélectionner. Re-cliquez pour la retirer.</p>
        <IconSelector v-model="(showEditQueueModal ? editingQueue : newQueue).icon" class="scale-90 origin-left" />
      </div>

      <div class="p-3 bg-slate-50 rounded-lg border border-slate-100">
        <div class="flex items-center gap-2 mb-3">
          <div class="w-1 h-3 bg-indigo-500 rounded-full"></div>
          <span class="text-[10px] font-bold text-slate-600 uppercase">Tickets</span>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex items-center gap-2">
            <span class="text-[9px] font-bold text-slate-400">MIN</span>
            <input v-model.number="(showEditQueueModal ? editingQueue : newQueue).rangeMin" type="number" class="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs font-bold text-slate-700 focus:border-indigo-500 outline-none" />
          </div>
          <div class="flex items-center gap-2">
            <span class="text-[9px] font-bold text-slate-400">MAX</span>
            <input v-model.number="(showEditQueueModal ? editingQueue : newQueue).rangeMax" type="number" class="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs font-bold text-slate-700 focus:border-indigo-500 outline-none" />
          </div>
        </div>
      </div>

      <div class="pt-2">
        <button @click="showEditQueueModal ? updateQueue() : createQueue()" class="w-full py-2.5 bg-slate-900 text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-md active:scale-95">
          {{ showEditQueueModal ? 'Mettre à jour' : 'Confirmer' }}
        </button>
      </div>
    </div>
  </div>
</div>

<div v-if="showAddCounterModal" class="fixed inset-0 z-[110] flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300" @click="showAddCounterModal = false"></div>
  <div class="relative bg-white w-full max-w-sm rounded-[2rem] p-8 shadow-2xl border border-white animate-in zoom-in-95 duration-200">
    <div class="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6">
      <UIIcon name="archive" :size="28" />
    </div>
    <h2 class="text-2xl font-bold text-slate-900 mb-2">Nouveau Guichet</h2>
    <p class="text-slate-400 text-xs mb-8 font-medium">Définissez un nom clair pour que les clients puissent identifier le poste.</p>
    
    <div class="space-y-6">
      <div class="group">
        <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-2 block group-focus-within:text-indigo-600 transition-colors">Nom du guichet</label>
        <input v-model="newCounter.name" placeholder="ex: Guichet 1, Caisse A..." class="w-full bg-slate-50 border border-slate-100 rounded-xl p-4 text-sm font-bold focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all" />
      </div>
      <button @click="createCounter" class="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase tracking-[0.2em] hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-200 transition-all active:scale-[0.98]">
        Créer le poste
      </button>
    </div>
  </div>
</div>

<div v-if="showAddUserModalState" class="fixed inset-0 z-[110] flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300" @click="showAddUserModalState = false"></div>
  <div class="relative bg-white w-full max-w-md rounded-[2.5rem] overflow-hidden shadow-2xl animate-in slide-in-from-bottom-8 duration-300">
    <div class="bg-slate-900 p-8 text-center relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-transparent"></div>
      <div class="relative z-10">
        <div class="w-20 h-20 bg-white/10 backdrop-blur-xl text-white rounded-3xl flex items-center justify-center mx-auto mb-4 border border-white/20 shadow-2xl">
          <span class="text-3xl">👤</span>
        </div>
        <h2 class="text-xl font-bold text-white">Ajouter un collaborateur</h2>
        <p class="text-indigo-200/60 text-[10px] font-bold uppercase tracking-widest mt-1">Accès Agence sécurisé</p>
      </div>
    </div>
    
    <div class="p-8 space-y-5">
      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-1.5">
          <label class="text-[10px] font-bold text-slate-400 uppercase ml-1">Prénom</label>
          <input v-model="newUser.firstName" class="w-full bg-slate-50 border-slate-100 rounded-xl p-3.5 text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
        </div>
        <div class="space-y-1.5">
          <label class="text-[10px] font-bold text-slate-400 uppercase ml-1">Nom</label>
          <input v-model="newUser.lastName" class="w-full bg-slate-50 border-slate-100 rounded-xl p-3.5 text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
        </div>
      </div>
      
      <div class="space-y-1.5">
        <label class="text-[10px] font-bold text-slate-400 uppercase ml-1">Email professionnel</label>
        <input v-model="newUser.email" type="email" class="w-full bg-slate-50 border-slate-100 rounded-xl p-3.5 text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
      </div>

      <div class="space-y-1.5 pb-2">
        <label class="text-[10px] font-bold text-slate-400 uppercase ml-1">Mot de passe provisoire</label>
        <div class="relative">
          <input v-model="newUser.password" type="password" class="w-full bg-slate-50 border-slate-100 rounded-xl p-3.5 text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
          <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300">🔒</span>
        </div>
      </div>
      
      <button @click="createUser" class="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all flex items-center justify-center gap-2">
        Créer l'utilisateur
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
      </button>
    </div>
  </div>
</div>

<div v-if="resetData.show" class="fixed inset-0 z-[120] flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" @click="resetData.show = false"></div>
  
  <div class="relative bg-white w-full max-w-[320px] rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
    <div class="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
      <div class="text-lg">🔑</div>
      <div>
        <h2 class="text-sm font-bold text-slate-800 leading-none">Réinitialisation</h2>
        <p class="text-[10px] text-slate-400 font-medium mt-1 uppercase tracking-wider">Sécurité agent</p>
      </div>
    </div>

    <div class="p-5">
      <p class="text-[11px] text-slate-500 mb-4 text-center">
        Code pour : <span class="font-bold text-slate-700 block mt-0.5 truncate">{{ resetData.email }}</span>
      </p>
      
      <div 
        @click="copyToClipboard(resetData.key)" 
        class="group relative bg-slate-50 p-4 rounded-lg border-2 border-dashed border-slate-200 cursor-pointer hover:bg-white hover:border-indigo-400 transition-all text-center mb-4"
      >
        <span class="text-2xl font-black tracking-widest text-indigo-600 select-all font-mono">
          {{ resetData.key }}
        </span>
        <div class="absolute -top-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <span class="bg-indigo-600 text-white text-[8px] font-bold px-2 py-0.5 rounded uppercase tracking-tighter">
            {{ copied ? 'Copié !' : 'Copier' }}
          </span>
        </div>
      </div>

      <div class="space-y-4">
        <div class="flex items-center justify-center gap-2 py-1 border-y border-slate-50">
          <span class="w-1.5 h-1.5 bg-emerald-500 rounded-full shadow-[0_0_5px_rgba(16,185,129,0.5)]"></span>
          <p class="text-[9px] font-bold text-slate-400 uppercase">
            Expire : {{ formatDate(resetData.expiresAt) }}
          </p>
        </div>

        <button 
          @click="resetData.show = false" 
          class="w-full py-2.5 bg-slate-900 text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-black transition-all active:scale-95 shadow-lg shadow-slate-200"
        >
          Fermer
        </button>
      </div>
    </div>
  </div>
</div>

  </div>
</template>
<script>
import { toast } from 'vue3-toastify';
import TablePagination from '@/components/TablePagination.vue';
import IconSelector from '@/components/IconSelector.vue';
import PharmacyIcon from '@/components/PharmacyIcons.vue';
import UIIcon from '@/components/UIIcons.vue';

export default {
  components: {
    TablePagination,
    IconSelector,
    PharmacyIcon,
    UIIcon
  },
  data() {
    return {
      activeTab: 'counters',
      selectedAgencyId: null,
      agencies: [],
      counters: [],
      users: [],
      queues: [], // Les services (Files A, B, C)
      showEditQueueModal: false,
    editingQueue: { _id: null, name: "", prefix: "", icon: "pill", rangeMin: 0, rangeMax: 999 },
    copied: false,

    // Pagination et recherche pour users
    userSearchQuery: '',
    userCurrentPage: 1,
    userItemsPerPage: 25,

    resetData: {
      show: false,
      key: '',
      email: '',
      expiresAt: null
    },

      selectedCounter: null,
      showAddCounterModal: false,
      showAddUserModalState: false,
      showAddQueueModal: false,

      newCounter: { name: "", serviceIds: [], active: true },
      newQueue: { name: "", prefix: "", icon: "pill", rangeMin: 0, rangeMax: 999 },
      newUser: { firstName: "", lastName: "", email: "", password: "", role: "EMPLOYEE", assignedCounterId: null }
    };
  },

  computed: {
    filteredUsers() {
      if (!this.userSearchQuery) return this.users;
      const query = this.userSearchQuery.toLowerCase();
      return this.users.filter(user =>
        user.firstName.toLowerCase().includes(query) ||
        user.lastName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query)
      );
    },
    paginatedUsers() {
      const start = (this.userCurrentPage - 1) * this.userItemsPerPage;
      const end = start + this.userItemsPerPage;
      return this.filteredUsers.slice(start, end);
    },
    filteredQueuesForDefault() {
      // Si pas de guichet sélectionné ou pas de services assignés, on ne retourne rien
      if (!this.selectedCounter || !this.selectedCounter.serviceIds) {
        return [];
      }

      // On filtre "queues" : on garde "q" si son _id est présent dans serviceIds
      return this.queues.filter(q =>
        this.selectedCounter.serviceIds.map(id => String(id)).includes(String(q._id))
      );
    }
  },

  watch: {
    userSearchQuery() {
      this.userCurrentPage = 1;
    }
  },

  methods: {
    async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      this.copied = true;
      toast.success("Clé copiée dans le presse-papier");
      
      // Réinitialise l'état visuel du bouton après 2 secondes
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    } catch (err) {
      toast.error("Impossible de copier la clé");
      console.error("Erreur copie:", err);
    }
  },
    async fetchAgencies() {
      try {
        const token = localStorage.getItem("superAdminToken");
        const res = await fetch("https://api.datafuse.fr/api/super-admin/agencies", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        this.agencies = data.map(a => ({ id: a._id, nom: a.name }));
        if (this.agencies.length > 0) {
          this.selectedAgencyId = this.agencies[0].id;
          this.fetchData();
        }
      } catch (err) { toast.error("Erreur agences"); }
    },

async fetchData() {
  if (!this.selectedAgencyId) return;
  
  const token = localStorage.getItem("superAdminToken");
  const h = { Authorization: `Bearer ${token}` };
  const baseUrl = "https://api.datafuse.fr/api/super-admin"; 

  try {
    const [resC, resU, resQ] = await Promise.all([
      fetch(`${baseUrl}/counters?agencyId=${this.selectedAgencyId}`, { headers: h }), 
      fetch(`${baseUrl}/users?agencyId=${this.selectedAgencyId}`, { headers: h }),
      // Correction probable de la route ici :
     fetch(`${baseUrl}/queues/${this.selectedAgencyId}`, { headers: h })
    ]);

    // On vérifie que les 3 réponses sont valides (statut 200-299)
    if (!resC.ok || !resU.ok || !resQ.ok) {
      throw new Error("Une des requêtes a échoué");
    }

    const [dataC, dataU, dataQ] = await Promise.all([
      resC.json(),
      resU.json(),
      resQ.json()
    ]);

    this.counters = dataC;
    this.users = dataU;
    this.queues = dataQ;

    console.log("✅ Sync réussie:", { counters: dataC, users: dataU, queues: dataQ });

  } catch (err) {
    console.error("❌ Erreur sync détaillée:", err);
    toast.error("Erreur de synchronisation des données");
  }
},

openEditQueueModal(queue) {
    this.editingQueue = {
      ...queue,
      rangeMin: queue.rangeMin !== undefined ? queue.rangeMin : 0,
      rangeMax: queue.rangeMax !== undefined ? queue.rangeMax : 999
    }; // Clone pour éviter la modification en direct
    this.showEditQueueModal = true;
  },

  // Logique de mise à jour API
  async updateQueue() {
    if (!this.editingQueue.name || !this.editingQueue.prefix) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    // Validation de la plage
    if (this.editingQueue.rangeMin >= this.editingQueue.rangeMax) {
      toast.error("Le début de la plage doit être inférieur à la fin");
      return;
    }

    try {
      const res = await fetch(`https://api.datafuse.fr/api/super-admin/queue/${this.editingQueue._id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify({
          name: this.editingQueue.name,
          prefix: this.editingQueue.prefix.toUpperCase(),
          icon: this.editingQueue.icon, // ✅ Peut être null si aucune icône sélectionnée
          rangeMin: this.editingQueue.rangeMin,
          rangeMax: this.editingQueue.rangeMax,
          agencyId: this.selectedAgencyId
        })
      });

      if (res.ok) {
        toast.success("File d'attente mise à jour");
        this.showEditQueueModal = false;
        this.fetchData(); // Rafraîchir la liste
      } else {
        const errorData = await res.json();
        toast.error(errorData.message || "Erreur lors de la mise à jour");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur réseau");
    }
  },

    // --- LOGIQUE PRIORITÉ FILES ---
    async toggleQueueToCounter(queueId) {
      // 1. On s'assure de travailler avec des Strings
      const qIdStr = String(queueId);

      if (!this.selectedCounter.serviceIds) {
        this.selectedCounter.serviceIds = [];
      }

      // 2. On cherche l'index avec le même type (String)
      const index = this.selectedCounter.serviceIds.indexOf(qIdStr);

      if (index > -1) {
        this.selectedCounter.serviceIds.splice(index, 1);
        // Reset si on supprime la file par défaut
        if (String(this.selectedCounter.defaultQueueId) === qIdStr) {
          this.selectedCounter.defaultQueueId = null;
        }
      } else {
        this.selectedCounter.serviceIds.push(qIdStr);
      }

      // 3. Mise à jour automatique dans le backend
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters/${this.selectedCounter._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({
            name: this.selectedCounter.name,
            serviceIds: this.selectedCounter.serviceIds,
            defaultQueueId: this.selectedCounter.defaultQueueId
          })
        });
        if (res.ok) {
          const queueName = this.queues.find(q => q._id === queueId)?.name;
          toast.success(`File "${queueName}" ${index > -1 ? 'retirée' : 'ajoutée'}`);
        }
      } catch (e) {
        console.error('Erreur toggleQueueToCounter:', e);
        toast.error("Erreur mise à jour");
      }
    },

    getQueueName(id) {
      const q = this.queues.find(q => q._id === id);
      return q ? `${q.prefix} - ${q.name}` : 'Inconnu';
    },

    async resetUserPassword(user) {
      
  try {
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${this.selectedAgencyId}/user/${user._id}/generate-reset-key`, {
      method: 'POST', // ou PUT selon ton routage
      headers: this.getHeaders()
    });

    if (res.ok) {
      const data = await res.json();
      // On remplit la modal avec les infos du back
      this.resetData = {
        show: true,
        key: data.oneTimeKey,
        email: data.userEmail,
        expiresAt: data.expiresAt
      };
      console.log(data)
      toast.success("Clé générée avec succès");
    } else {
      const err = await res.json();
      toast.error(err.error || "Erreur lors de la génération");
    }
  } catch (e) {
    console.error(e);
    toast.error("Erreur serveur");
  }
}, 

    // --- ACTIONS API (SERVICES / QUEUES) ---
    async createQueue() {
    console.log("creation de fil appeler ")

      // Validation de la plage
      if (this.newQueue.rangeMin >= this.newQueue.rangeMax) {
        toast.error("Le début de la plage doit être inférieur à la fin");
        return;
      }

      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/queue`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({ ...this.newQueue, agencyId: this.selectedAgencyId })
        });
        if (res.ok) {
          toast.success("File créée");
          this.showAddQueueModal = false;
          this.newQueue = { name: "", prefix: "", icon: "pill", rangeMin: 0, rangeMax: 999 };
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    // --- ACTIONS API (GUICHETS) ---
    async createCounter() {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({ ...this.newCounter, agencyId: this.selectedAgencyId })
        });
        if (res.ok) {
          this.showAddCounterModal = false;
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async updateCounter() {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters/${this.selectedCounter._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify(this.selectedCounter)
        });
        if (res.ok) {
          toast.success("Mis à jour");
          this.selectedCounter = null;
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async updateDefaultQueue() {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters/${this.selectedCounter._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({
            name: this.selectedCounter.name,
            defaultQueueId: this.selectedCounter.defaultQueueId,
            serviceIds: this.selectedCounter.serviceIds
          })
        });
        if (res.ok) {
          const queueName = this.selectedCounter.defaultQueueId
            ? this.queues.find(q => q._id === this.selectedCounter.defaultQueueId)?.name
            : 'Aucune';
          toast.success(`File par défaut : ${queueName}`);
          this.fetchData();
        }
      } catch (e) {
        console.error('Erreur updateDefaultQueue:', e);
        toast.error("Erreur liaison");
      }
    },

    // --- ACTIONS API (USERS) ---
    async createUser() {
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/users`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({ ...this.newUser, agencyId: this.selectedAgencyId })
        });
        if (res.ok) {
          this.showAddUserModalState = false;
          this.newUser = { firstName: "", lastName: "", email: "", password: "", role: "EMPLOYEE" };
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async updateUserCounter(user, event) {
      const cid = event.target.value === "null" ? null : event.target.value;
      try {
        await fetch(`https://api.datafuse.fr/api/super-admin/users/${user._id}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({ assignedCounterId: cid })
        });
        this.fetchData();
      } catch (e) { toast.error("Erreur"); }
    },

    async deleteCounter(id) {
      if (!confirm("Supprimer ce guichet ?")) return;
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/counters/${id}`, {
          method: 'DELETE',
          headers: this.getHeaders()
        });
        if (res.ok) {
          toast.success("Guichet supprimé");
          this.selectedCounter = null;
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async deleteQueue(id) {
      if (!confirm("Supprimer cette file d'attente ?")) return;
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/queue/${id}`, {
          method: 'DELETE',
          headers: this.getHeaders()
        });
        if (res.ok) {
          toast.success("File supprimée");
          this.fetchData();
        }
      } catch (e) { toast.error("Erreur"); }
    },

    async deleteUser(id) {
      if (!confirm("Supprimer cet utilisateur ?")) return;
      try {
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/users/${id}`, {
          method: 'DELETE',
          headers: this.getHeaders()
        });

        if (res.ok) {
          toast.success("Utilisateur supprimé");
          this.fetchData();
        } else {
          const errorData = await res.json();
          toast.error(errorData.error || "Erreur lors de la suppression");
        }
      } catch (e) {
        console.error("Erreur suppression:", e);
        toast.error("Erreur de connexion au serveur");
      }
    },

    formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
},

    // --- HELPERS ---
    getHeaders() {
      return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem("superAdminToken")}` };
    },
    getCounterUsers(id) { return this.users.filter(u => u.assignedCounterId === id); },
openEditDrawer(counter) { 
  const cloned = JSON.parse(JSON.stringify(counter));

  // 🔥 NORMALISATION DES IDS
  cloned.serviceIds = (cloned.serviceIds || []).map(id => String(id));
  cloned.defaultQueueId = cloned.defaultQueueId ? String(cloned.defaultQueueId) : null;

  this.selectedCounter = cloned;
}
 },

  mounted() { this.fetchAgencies(); }
};
</script>